import React from 'react';
import styled from 'styled-components';
import { P2, P3 } from '../../components/Typography';

interface OrganizationBrandingReadOnlySummaryProps {
  emailFooterText: string;
  friendlyOrganizationName: string;
  logoUrl: string;
}

const ReadOnlySummary = styled.div`
  background: ${(props) => props.theme.colors.bgAccent};
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 8px;
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  overflow: hidden;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ReadOnlyLogo = styled.div`
  align-items: center;
  background: #fff;
  border-right: 1px solid rgba(58, 49, 94, 0.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  min-height: 180px;
  padding: 20px;

  img {
    display: block;
    max-height: 96px;
    max-width: 220px;
    object-fit: contain;
  }

  @media (max-width: 720px) {
    border-bottom: 1px solid rgba(58, 49, 94, 0.1);
    border-right: 0;
  }
`;

const ReadOnlyDetails = styled.div`
  display: grid;
  gap: 14px;
  min-width: 0;
  padding: 20px;
`;

const ReadOnlyValue = styled(P2)`
  color: ${(props) => props.theme.colors.body};
  margin: 2px 0 0;
  white-space: pre-wrap;
`;

const ReadOnlyLabel = styled(P3)`
  color: ${(props) => props.theme.colors.placeholder};
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0;
  text-transform: uppercase;
`;

const ReadOnlyHeader = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
`;

const ReadOnlyTitle = styled(P2)`
  color: ${(props) => props.theme.colors.body};
  font-weight: 700;
  margin: 0;
`;

const ReadOnlyStatus = styled(P3)`
  background: rgba(102, 94, 131, 0.1);
  border: 1px solid rgba(102, 94, 131, 0.18);
  border-radius: 999px;
  color: ${(props) => props.theme.colors.body2};
  font-weight: 700;
  margin: 0;
  padding: 4px 9px;
`;

const ReadOnlyField = styled.div`
  background: #fff;
  border: 1px solid rgba(58, 49, 94, 0.08);
  border-radius: 6px;
  padding: 12px 14px;
`;

const EmptyLogo = styled(P3)`
  color: ${(props) => props.theme.colors.placeholder};
  margin: 0;
  text-align: center;
`;

const OrganizationBrandingReadOnlySummary: React.FC<OrganizationBrandingReadOnlySummaryProps> = ({
  emailFooterText,
  friendlyOrganizationName,
  logoUrl,
}) => {
  const hasPublishedBranding = !!logoUrl || !!emailFooterText;

  return (
    <ReadOnlySummary>
      <ReadOnlyLogo>
        {logoUrl ? <img alt="Email header logo preview" src={logoUrl} /> : <EmptyLogo>No logo selected</EmptyLogo>}
        <ReadOnlyStatus>{hasPublishedBranding ? 'Published branding' : 'No branding profile'}</ReadOnlyStatus>
      </ReadOnlyLogo>
      <ReadOnlyDetails>
        <ReadOnlyHeader>
          <ReadOnlyTitle>Organisation branding</ReadOnlyTitle>
          <ReadOnlyStatus>View only</ReadOnlyStatus>
        </ReadOnlyHeader>
        <ReadOnlyField>
          <ReadOnlyLabel>Friendly name</ReadOnlyLabel>
          <ReadOnlyValue>{friendlyOrganizationName || 'Not set'}</ReadOnlyValue>
        </ReadOnlyField>
        <ReadOnlyField>
          <ReadOnlyLabel>Footer text</ReadOnlyLabel>
          <ReadOnlyValue>{emailFooterText || 'Not set'}</ReadOnlyValue>
        </ReadOnlyField>
      </ReadOnlyDetails>
    </ReadOnlySummary>
  );
};

export default OrganizationBrandingReadOnlySummary;
